<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import { useApiClient } from "@/api/client";
import FailedPriceUpdateDTO = Modules.BookingExperts.Data.FailedPriceUpdateDTO;
import dayjs from "dayjs";
import { getFlagMessage } from "@/modules/booking_experts/utils/priceUpdateFlags";

const api = useApiClient("bookingExperts");

const items = ref<(FailedPriceUpdateDTO & { errors: string[] })[]>([]);
const isLoading = ref(true);

const headers = [
	{
		title: "Pricelist",
		align: "start",
		key: "pricelistName",
		sortable: false,
	},
	{
		title: "Timestamp",
		align: "start",
		key: "updatedAt",
		sortable: false,
	},
	{
		title: "Errors",
		align: "start",
		key: "errors",
		sortable: false,
	},
	{
		title: "Actions",
		align: "end",
		key: "actions",
		sortable: false,
	},
];

async function getFailedCurveUpdates() {
	const response = await api.getFailedCurveUpdates({ limit: 4 });
	if (response.isOk()) {
		items.value = response.value.map((curveUpdate) => ({
			...curveUpdate,
			errors: getFlagMessage(curveUpdate.updateFlags),
		}));
	}
	isLoading.value = false;
}
onBeforeMount(getFailedCurveUpdates);
</script>

<template>
	<v-card max-height="360" class="card" ref="el">
		<v-card-title class="bg-background" style="height: 48px"
			>Recent pricing errors</v-card-title
		>

		<v-card-text class="px-2 py-0">
			<v-data-table
				density="compact"
				hide-default-header
				hide-default-footer
				:items="items"
				:headers="headers"
				:loading="isLoading"
				items-per-page="4"
			>
				<template #no-data>
					<div class="my-3">There are currently no pricing errors</div>
				</template>
				<template #item.updatedAt="{ item }">
					{{ dayjs(item.updatedAt).format("D MMM HH:mm") }}
				</template>
				<template #item.errors="{ item }">
					<div class="d-flex align-center ga-2">
						<div>
							{{ item.errors?.[0] }}
						</div>
						<v-tooltip v-if="item.errors.length > 1">
							<template #activator="{ props }">
								<v-chip
									v-bind="props"
									size="small"
									color="secondary"
									variant="flat"
								>
									+{{ item.errors.length - 1 }}
								</v-chip>
							</template>
							<ul class="pl-2">
								<li v-for="(error, i) in item.errors" :key="i">
									{{ error }}
								</li>
							</ul>
						</v-tooltip>
					</div>
				</template>
				<template #item.actions="{ item }">
					<v-btn
						v-tooltip="`Open overview for ${item.pricelistName}`"
						color="on-surface"
						variant="text"
						density="compact"
						icon
						:to="`/pricing/apply/${item.pricelistId}?los=${item.lengthOfStay}`"
					>
						<v-icon size="15"> fa: fal fa-arrow-right </v-icon>
					</v-btn>
				</template>
			</v-data-table>
			<v-divider opacity="0.25" />
		</v-card-text>
		<div class="d-flex pa-2">
			<v-spacer />
			<v-chip variant="flat" to="/pricing/pricelists">
				Go to pricelists
			</v-chip>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.card {
	display: grid;
	grid-template-rows: auto 1fr;
}
</style>
